// formularioContratoVista.tsx
import React, { useEffect } from "react";
import {
    Box,
    Grid,
    TextInput,
    NumberInput,
    Textarea,
    Select,
    Group,
    Button,
    Text
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconCancel, IconDeviceFloppy, IconFileIsr, IconPencilCheck } from "@tabler/icons-react";
import type { IFormularioContratoVistaProps } from "../interfaces";
import dayjs from "dayjs";
import { useProyecto } from "../../proyectos/contextoProyecto";
import { EnumTipoContrato } from "../enums/enumTipoContrato";


const FormularioContratoVista: React.FC<IFormularioContratoVistaProps> = ({
    contratoSeleccionado,
    manejarCambioValorContrato,
    agregarError,
    errores,
    tiposContrato,
    guardarEditarContrato,
    cambiarContrato,
    enCambioContrato,
    consultarContratoActivo
}) => {
    const { proyecto } = useProyecto();

    useEffect(() => {
        if (proyecto.ContratoId !== null) {
            consultarContratoActivo(proyecto.ContratoId || 0);
        }
    }, []);

    return (
        <Box>
            {contratoSeleccionado.Id !== 0 && proyecto.ContratoId !== null ? (
                <Group justify="flex-start" pb="sm">
                    <Button
                        onClick={() => cambiarContrato(true)}
                        color="primary"
                        variant="light"
                        size='compact-sm'
                        rightSection={<IconFileIsr opacity={100} size={15} />}
                        radius="lg"
                    >
                        <Text fw={500} fz={11}>Cambiar Contrato</Text>
                    </Button>
                </Group>) : (
                proyecto.ContratoId !== null && contratoSeleccionado.Id === 0 && enCambioContrato && (
                    <Group justify="flex-start" pb="sm">
                        <Button
                            onClick={() => cambiarContrato(false)}
                            color="primary"
                            variant="light"
                            size='compact-sm'
                            rightSection={<IconCancel opacity={100} size={15} />}
                            radius="lg"
                        >
                            <Text fw={500} fz={11}>Cancelar Cambio</Text>
                        </Button>
                    </Group>
                )
            )}
            <Grid gutter="md">
                <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                        label="Número Contrato"
                        placeholder="Ej: 2023-001.1"
                        size="xs"
                        maxLength={20}
                        value={contratoSeleccionado.Numero}
                        onChange={(e) => {
                            const value = e.currentTarget.value;
                            manejarCambioValorContrato("Numero", value);
                            if (!value) {
                                agregarError("Numero", "Este campo es requerido");
                            } else {
                                agregarError("Numero", "");
                            }
                        }}
                        error={errores.Numero}
                        required
                    />

                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 4 }}>
                    <Select
                        label="Tipo Contrato"
                        placeholder="Seleccione"
                        size="xs"
                        comboboxProps={{ zIndex: 5000 }}
                        data={tiposContrato.map((t) => ({ value: t.Id.toString(), label: t.Nombre }))}
                        value={contratoSeleccionado.TipoContratoId ? contratoSeleccionado.TipoContratoId.toString() : null}
                        onChange={(value) => {
                            const nuevoTipo = value ? Number(value) : 0;
                            manejarCambioValorContrato("TipoContratoId", nuevoTipo);

                            // Si cambia a un tipo diferente de Instalación, limpiar campos exclusivos
                            if (nuevoTipo !== EnumTipoContrato.Instalacion) {
                                manejarCambioValorContrato("FechaInicioContractual", null);
                                manejarCambioValorContrato("DiasInstalacionSegunContrato", null);
                                manejarCambioValorContrato("FechaFinContractual", null);
                                manejarCambioValorContrato("FechaTentativaEntregaCliente", null);
                            }

                            if (!value) {
                                agregarError("TipoContratoId", "Selecciona un tipo de contrato");
                            } else {
                                agregarError("TipoContratoId", "");
                            }
                        }}
                        error={errores.TipoContratoId}
                        required
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 4 }}>
                    <DateInput
                        label="Fecha Inicio"
                        valueFormat="DD/MM/YYYY"
                        size="xs"
                        popoverProps={{ zIndex: 10000 }}
                        value={contratoSeleccionado.FechaInicio}
                        onChange={(value) => {
                            manejarCambioValorContrato("FechaInicio", value);

                            if (value && contratoSeleccionado.VigenciaMeses) {
                                const fechaFin = dayjs(value)
                                    .add(contratoSeleccionado.VigenciaMeses, "month")
                                    .toDate();

                                manejarCambioValorContrato("FechaFin", fechaFin);
                            } else {
                                manejarCambioValorContrato("FechaFin", null);
                            }
                        }}
                        error={errores.FechaInicio}
                        required
                    />

                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 4 }}>
                    <NumberInput
                        label="Vigencia (Meses)"
                        size="xs"
                        min={0}
                        maxLength={4}
                        value={contratoSeleccionado.VigenciaMeses}
                        disabled={!contratoSeleccionado.FechaInicio}
                        onChange={(value) => {
                            manejarCambioValorContrato("VigenciaMeses", value ?? 0);

                            if (!value) {
                                agregarError("VigenciaMeses", "Este campo es requerido");
                                manejarCambioValorContrato("FechaFin", null);
                                return;
                            }

                            agregarError("VigenciaMeses", "");

                            if (contratoSeleccionado.FechaInicio) {
                                const fechaFin = dayjs(contratoSeleccionado.FechaInicio)
                                    .add(Number(value), "month")
                                    .toDate();

                                manejarCambioValorContrato("FechaFin", fechaFin);
                            }
                        }}
                        error={errores.VigenciaMeses}
                        required
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 4 }}>
                    <DateInput
                        label="Fecha Fin"
                        valueFormat="DD/MM/YYYY"
                        size="xs"
                        popoverProps={{ disabled: true }}
                        disabled
                        value={contratoSeleccionado.FechaFin}
                        error={errores.FechaFin}
                        required
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 4 }}>
                    <NumberInput
                        min={0}
                        value={contratoSeleccionado.ValorMensual}
                        onChange={(value) => {
                            manejarCambioValorContrato("ValorMensual", value ?? 0)
                        }}
                        size="xs"
                        prefix="$"
                        thousandSeparator="."
                        decimalSeparator=","
                        maxLength={18}
                        label="Valor Mensual"
                        hideControls={false}
                        leftSectionWidth={78}
                        leftSection={
                            <Select
                                data={[
                                    { value: "COP", label: "COP" },
                                    { value: "USD", label: "USD" },
                                ]}
                                value={contratoSeleccionado.Moneda || "COP"}
                                onChange={(v) => {
                                    manejarCambioValorContrato("Moneda", v ? v : 'COP')
                                }}
                                size="xs"
                                styles={{
                                    input: {
                                        width: "100%",
                                        height: "100%",
                                        border: "none",
                                        background: "transparent",
                                        fontSize: "12px",
                                        padding: "0 2px",
                                        textAlign: "center",
                                    },
                                    dropdown: { zIndex: 9999 },
                                }}
                            />
                        }
                        // styles={{
                        //     root: {
                        //         width: "100%",
                        //         display: "flex",
                        //         alignItems: "center",
                        //         flexDirection: 'column'
                        //     },
                        //     section: {
                        //         display: "flex",
                        //         alignItems: "center",
                        //         // justifyContent: "center",
                        //         padding: 0,
                        //     },
                        // }}
                    />
                </Grid.Col>

                <Grid.Col span={12}>
                    <Textarea
                        label="Condiciones Facturación"
                        autosize
                        maxLength={150}
                        minRows={2}
                        maxRows={4}
                        size="xs"
                        value={contratoSeleccionado.CondicionesFacturacion}
                        onChange={(e) =>
                            manejarCambioValorContrato("CondicionesFacturacion", e.target.value)
                        }
                    // error={errores.CondicionesFacturacion}
                    />
                </Grid.Col>

                {contratoSeleccionado.TipoContratoId === EnumTipoContrato.Instalacion && (
                    <>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <DateInput
                                label="Fecha Inicio Contractual"
                                valueFormat="DD/MM/YYYY"
                                size="xs"
                                placeholder="Seleccione una fecha"
                                popoverProps={{ zIndex: 10000 }}
                                value={contratoSeleccionado.FechaInicioContractual ? new Date(contratoSeleccionado.FechaInicioContractual) : null}
                                onChange={(value) => {
                                    manejarCambioValorContrato("FechaInicioContractual", value);
                                    if (value && contratoSeleccionado.DiasInstalacionSegunContrato) {
                                        const fechaFin = dayjs(value)
                                            .add(contratoSeleccionado.DiasInstalacionSegunContrato, "day")
                                            .toDate();
                                        manejarCambioValorContrato("FechaFinContractual", fechaFin);
                                    } else if (!value) {
                                        manejarCambioValorContrato("FechaFinContractual", null);
                                    }
                                }}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <NumberInput
                                label="Días Instalación (Contrato)"
                                size="xs"
                                placeholder="Ej: 30"
                                min={0}
                                value={contratoSeleccionado.DiasInstalacionSegunContrato ?? ''}
                                disabled={!contratoSeleccionado.FechaInicioContractual}
                                onChange={(value) => {
                                    manejarCambioValorContrato("DiasInstalacionSegunContrato", value === '' ? null : Number(value));
                                    if (value !== '' && contratoSeleccionado.FechaInicioContractual) {
                                        const fechaFin = dayjs(contratoSeleccionado.FechaInicioContractual)
                                            .add(Number(value), "day")
                                            .toDate();
                                        manejarCambioValorContrato("FechaFinContractual", fechaFin);
                                    } else if (value === '') {
                                        manejarCambioValorContrato("FechaFinContractual", null);
                                    }
                                }}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <DateInput
                                label="Fecha Fin Contractual"
                                valueFormat="DD/MM/YYYY"
                                size="xs"
                                placeholder="Cálculo automático"
                                disabled
                                popoverProps={{ disabled: true }}
                                value={contratoSeleccionado.FechaFinContractual ? new Date(contratoSeleccionado.FechaFinContractual) : null}
                            />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <DateInput
                                label="Fecha Tentativa Entrega Cliente"
                                valueFormat="DD/MM/YYYY"
                                size="xs"
                                placeholder="Seleccione una fecha"
                                popoverProps={{ zIndex: 10000 }}
                                value={contratoSeleccionado.FechaTentativaEntregaCliente ? new Date(contratoSeleccionado.FechaTentativaEntregaCliente) : null}
                                onChange={(value) => manejarCambioValorContrato("FechaTentativaEntregaCliente", value)}
                            />
                        </Grid.Col>
                    </>
                )}
            </Grid>
            <Box pos="sticky" bottom={0} bg="white">
                <Group justify="flex-end" pt="sm">
                    <Button
                        onClick={guardarEditarContrato}
                        color="primary"
                        variant="filled"
                        size='compact-sm'
                        rightSection={contratoSeleccionado.Id ? <IconPencilCheck opacity={100} color="white" size={15} /> : <IconDeviceFloppy opacity={100} color="white" size={15} />}
                        radius="lg"
                    >
                        <Text fw={500} fz={11}>{contratoSeleccionado.Id ? "Editar Contrato" : "Guardar Contrato"}</Text>
                    </Button>
                </Group>
            </Box>
        </Box>
    );
};

export default FormularioContratoVista;
